import React, { Component } from 'react';
import { Check, Star, ShieldCheck, Truck, Clock, AlertCircle, ArrowRight } from 'lucide-react';
// --- Reusable Components ---
const CtaButton = ({
  children,
  className = '',
  fullWidth = false
}: {
  children: React.ReactNode;
  className?: string;
  fullWidth?: boolean;
}) => <a href="https://zenessa.shop/products/anti-aging-essence" target="_blank" rel="noopener noreferrer" className={`
    bg-[#0d9488] hover:bg-[#0f766e] text-white font-bold py-4 px-8 rounded-lg shadow-lg 
    transition-all transform hover:-translate-y-0.5 hover:shadow-xl text-lg md:text-xl
    flex items-center justify-center gap-2
    ${fullWidth ? 'w-full' : 'w-auto'}
    ${className}
  `} data-id="element-2">
    {children}
    <ArrowRight className="w-5 h-5" data-id="element-3" />
  </a>;
const Section = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <section className={`mb-12 md:mb-16 ${className}`} data-id="element-4">{children}</section>;
const HighlightBox = ({
  children,
  title,
  type = 'info'
}: {
  children: React.ReactNode;
  title?: string;
  type?: 'info' | 'warning' | 'success';
}) => {
  const colors = {
    info: 'bg-blue-50 border-blue-200 text-blue-900',
    warning: 'bg-amber-50 border-amber-200 text-amber-900',
    success: 'bg-green-50 border-green-200 text-green-900',
    gold: 'bg-yellow-50 border-yellow-200 text-yellow-900'
  };
  const borderColors = {
    info: 'border-l-4 border-blue-500',
    warning: 'border-l-4 border-amber-500',
    success: 'border-l-4 border-green-500',
    gold: 'border-l-4 border-yellow-500'
  };
  return <div className={`p-6 my-8 rounded-r-lg ${colors[type as keyof typeof colors]} ${borderColors[type as keyof typeof borderColors]} shadow-sm`} data-id="element-5">
      {title && <h4 className="font-bold text-lg mb-2 flex items-center gap-2" data-id="element-6">
          {type === 'warning' && <AlertCircle className="w-5 h-5" data-id="element-7" />}
          {title}
        </h4>}
      <div className="text-base leading-relaxed" data-id="element-8">{children}</div>
    </div>;
};
const Testimonial = ({
  name,
  age,
  quote,
  image,
  result
}: {
  name: string;
  age: string;
  quote: string;
  image: string;
  result?: string;
}) => <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 my-8" data-id="element-9">
    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-4" data-id="element-10">
      <img src={image} alt={name} className="w-16 h-16 rounded-full object-cover border-2 border-gray-200" data-id="element-11" />
      <div data-id="element-12">
        <div className="flex text-yellow-400 mb-1" data-id="element-13">
          {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-14" />)}
        </div>
        <p className="font-bold text-gray-900" data-id="element-15">
          {name}, {age}
        </p>
        <p className="text-sm text-gray-500" data-id="element-16">Verified Buyer</p>
      </div>
    </div>
    <blockquote className="italic text-gray-700 mb-4 text-lg" data-id="element-17">
      "{quote}"
    </blockquote>
    {result && <div className="bg-green-50 text-green-800 px-4 py-2 rounded-md text-sm font-medium inline-block" data-id="element-18">
        Result: {result}
      </div>}
  </div>;
const BeforeAfter = ({
  label,
  image1,
  image2,
  caption
}: {
  label: string;
  image1: string;
  image2: string;
  caption?: string;
}) => <div className="my-10" data-id="element-19">
    <h4 className="text-center font-bold text-gray-900 mb-4 uppercase tracking-wide text-sm" data-id="element-20">
      {label}
    </h4>
    <div className="grid grid-cols-2 gap-2 md:gap-4" data-id="element-21">
      <div className="relative group" data-id="element-22">
        <img src={image1} alt="Before" className="w-full h-48 md:h-64 object-cover rounded-lg shadow-md" data-id="element-23" />
        <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded" data-id="element-24">
          BEFORE
        </div>
      </div>
      <div className="relative group" data-id="element-25">
        <img src={image2} alt="After" className="w-full h-48 md:h-64 object-cover rounded-lg shadow-md" data-id="element-26" />
        <div className="absolute bottom-2 left-2 bg-green-600/90 text-white text-xs px-2 py-1 rounded" data-id="element-27">
          AFTER 12 WEEKS
        </div>
      </div>
    </div>
    {caption && <p className="text-center text-sm text-gray-500 mt-3 italic" data-id="element-28">{caption}</p>}
  </div>;
// --- Main Page Component ---
export function AdvertorialPage() {
  return <div className="min-h-screen bg-[#fefdfb] font-sans text-[#333]" data-id="element-29">
      {/* Top Bar */}
      <div className="bg-black text-white text-center py-2 text-xs uppercase tracking-widest font-semibold" data-id="element-30">
        Advertorial • Health & Beauty • Science
      </div>

      <main className="max-w-[700px] mx-auto px-5 py-8 md:py-12" data-id="element-31">
        {/* Header Section */}
        <header className="mb-10 text-center md:text-left" data-id="element-32">
          <span className="text-[#0d9488] font-bold tracking-wider text-sm uppercase mb-3 block" data-id="element-33">
            Skin Health Discovery
          </span>
          <h1 className="font-serif text-3xl md:text-5xl font-bold leading-tight text-gray-900 mb-6" data-id="element-34">
            Scientists Discover Why Your Anti-Aging Creams Can't Work{' '}
            <span className="text-[#0d9488] italic" data-id="element-35">
              (And the 'Grounding' Technology That Actually Can)
            </span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed font-medium" data-id="element-36">
            Breakthrough research reveals the cellular reason retinol, vitamin
            C, and hyaluronic acid fail—and introduces the natural solution
            dermatologists are quietly recommending.
          </p>

          <div className="flex items-center gap-3 mt-6 text-sm text-gray-500 border-t border-b border-gray-200 py-3" data-id="element-37">
            <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&q=80&w=100&h=100" alt="Author" className="w-10 h-10 rounded-full" data-id="element-38" />
            <div data-id="element-39">
              <p className="font-semibold text-gray-900" data-id="element-40">By Sarah Jenkins</p>
              <p data-id="element-41">Health & Science Editor</p>
            </div>
            <div className="ml-auto text-gray-400" data-id="element-42">
              Updated: January 27, 2026
            </div>
          </div>
        </header>

        {/* Hero Image */}
        <div className="mb-10" data-id="element-43">
          <img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80&w=1200&h=600" alt="Scientist examining skin cells" className="w-full h-auto rounded-xl shadow-lg mb-2" data-id="element-44" />
          <p className="text-xs text-gray-500 text-right" data-id="element-45">
            Photo: Medical Research Institute
          </p>
        </div>

        {/* Introduction */}
        <article className="prose prose-lg prose-stone max-w-none" data-id="element-46">
          <p className="lead text-xl md:text-2xl font-serif text-gray-800 mb-6" data-id="element-47">
            If you've spent hundreds—or thousands—of dollars on anti-aging
            creams that promised results but delivered nothing, you're not
            alone. And more importantly,{' '}
            <span className="font-bold underline decoration-[#0d9488] decoration-2 underline-offset-2" data-id="element-48">
              it's not your fault.
            </span>
          </p>

          <p data-id="element-49">
            You tried the retinols, vitamin C serums, hyaluronic acids, peptide
            creams. Maybe even prescription tretinoin. And what did you get?
          </p>

          <p className="font-bold italic text-gray-900 border-l-4 border-[#0d9488] pl-4 my-6" data-id="element-50">
            As one frustrated woman put it: "The only visible change was my bank
            balance."
          </p>

          <p data-id="element-51">
            Recently, dermatological researchers made a shocking discovery about
            why these products fail. And it has nothing to do with the
            ingredients themselves.
          </p>

          <p data-id="element-52">
            What you're about to read reveals a solution that's helping women
            over 45 achieve firmer, smoother skin they thought was impossible
            without Botox or surgery.
          </p>

          {/* Problem Section */}
          <h2 className="font-serif text-3xl font-bold text-gray-900 mt-12 mb-6" data-id="element-53">
            Why Everything You've Tried Has Failed
          </h2>

          <p data-id="element-54">
            For decades, skincare companies told us the problem was simple:
            aging skin loses collagen, so we need products with retinol and
            peptides to "boost" production.
          </p>
          <p data-id="element-55">
            You've tried them all—drugstore and prestige brands. You were
            diligent. Applied them morning and night. Waited for results.
          </p>
          <p data-id="element-56">
            But nothing changed. Or worse—you got irritation but still no
            visible improvement.
          </p>

          <HighlightBox title="The Industry Secret" type="warning" data-id="element-57">
            Research shows that 68% of women over 45 make significant
            investments in beauty regimes. Yet most expect these creams to be
            ineffective—even as they keep trying them "just in case."
          </HighlightBox>

          {/* The Real Problem */}
          <h2 className="font-serif text-3xl font-bold text-gray-900 mt-12 mb-6" data-id="element-58">
            The Real Problem Nobody Talked About
          </h2>

          <p data-id="element-59">
            Recently, researchers discovered: The real problem is your skin's
            ability to <strong data-id="element-60">absorb and utilize</strong> anti-aging
            ingredients at the cellular level.
          </p>
          <p data-id="element-61">
            As we age—especially during menopause—our skin's cellular
            communication breaks down. Environmental stressors create chronic
            inflammation. Hormonal changes disrupt cellular signaling.
          </p>
          <p data-id="element-62">
            The result? Even the most powerful ingredients can't penetrate
            deeply enough to trigger real collagen production. They sit on the
            surface. They might hydrate temporarily. But they can't create
            lasting change because they never reach the cellular level where
            collagen is actually produced.
          </p>

          <div className="bg-gray-100 p-6 rounded-lg my-8" data-id="element-63">
            <div className="flex gap-4" data-id="element-64">
              <div className="flex-shrink-0" data-id="element-65">
                <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&q=80&w=100&h=100" className="w-16 h-16 rounded-full object-cover" alt="Dermatologist" data-id="element-66" />
              </div>
              <div data-id="element-67">
                <p className="font-serif text-lg italic text-gray-800 mb-2" data-id="element-68">
                  "For years, I told patients: 'No cream will give you those
                  results.' I was right—because we didn't understand the
                  absorption problem."
                </p>
                <p className="text-sm font-bold text-gray-600" data-id="element-69">
                  — Dr. Jane Smith, Board-Certified Dermatologist
                </p>
              </div>
            </div>
          </div>

          <p data-id="element-70">
            This explains everything: why products with "proven" ingredients did
            nothing. Why results faded after weeks. Why you felt procedures were
            your only option.
          </p>

          {/* The Discovery */}
          <h2 className="font-serif text-3xl font-bold text-gray-900 mt-12 mb-6" data-id="element-71">
            The Discovery That Changed Everything
          </h2>

          <p data-id="element-72">
            Dr. Jane Smith, a board-certified dermatologist, first heard about
            the solution at a 2019 medical conference. Researchers studying
            chronic inflammation presented findings on "grounding therapy"—also
            known as earthing.
          </p>
          <p data-id="element-73">
            She was skeptical. But participants weren't just reporting reduced
            inflammation—they showed improvements in skin quality: better
            texture, improved healing, more radiance.
          </p>
          <p data-id="element-74">
            Her team investigated the mechanism. What they discovered was
            remarkable.
          </p>

          <h3 className="font-bold text-xl text-gray-900 mt-8 mb-4" data-id="element-75">
            What Is Grounding Technology?
          </h3>
          <p data-id="element-76">
            Your body conducts electrical signals—this is how cells communicate.
            Modern life has disconnected us from the earth's natural electrical
            field. Research shows this contributes to chronic inflammation.
          </p>
          <p data-id="element-77">
            And here's the crucial part:{' '}
            <strong data-id="element-78">
              chronic inflammation blocks the absorption of anti-aging
              ingredients.
            </strong>
          </p>

          <img src="https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?auto=format&fit=crop&q=80&w=800&h=500" alt="Skin cellular structure diagram" className="w-full rounded-lg shadow-md my-8" data-id="element-79" />

          <p data-id="element-80">
            When Dr. Smith's team applied grounding principles to skincare, they
            discovered: grounding technology creates optimal conditions for
            cellular absorption.
          </p>
          <p data-id="element-81">
            It reduces the inflammation blocking ingredient penetration.
            Restores cellular communication. Essentially "turns on" your skin's
            receptivity to anti-aging ingredients.
          </p>

          {/* Comparison Table */}
          <div className="my-12 overflow-hidden border border-gray-200 rounded-xl shadow-sm" data-id="element-82">
            <div className="bg-gray-900 text-white p-4 text-center font-bold font-serif text-xl" data-id="element-83">
              Why This Works When Everything Else Failed
            </div>
            <div className="divide-y divide-gray-100" data-id="element-84">
              <div className="p-4 bg-white flex gap-4" data-id="element-85">
                <div className="text-red-500 font-bold w-32 flex-shrink-0" data-id="element-86">
                  Traditional Retinol
                </div>
                <div className="text-gray-600" data-id="element-87">
                  Works on surface. Limited penetration. Often causes
                  irritation.
                </div>
              </div>
              <div className="p-4 bg-gray-50 flex gap-4" data-id="element-88">
                <div className="text-red-500 font-bold w-32 flex-shrink-0" data-id="element-89">
                  Vitamin C
                </div>
                <div className="text-gray-600" data-id="element-90">
                  Unstable, inconsistent absorption. Initial brightening but no
                  long-term firming.
                </div>
              </div>
              <div className="p-4 bg-white flex gap-4" data-id="element-91">
                <div className="text-red-500 font-bold w-32 flex-shrink-0" data-id="element-92">
                  Hyaluronic Acid
                </div>
                <div className="text-gray-600" data-id="element-93">
                  Hydrates surface. Doesn't boost collagen production.
                </div>
              </div>
              <div className="p-4 bg-[#f0fdfa] flex gap-4 border-l-4 border-[#0d9488]" data-id="element-94">
                <div className="text-[#0d9488] font-bold w-32 flex-shrink-0" data-id="element-95">
                  Grounding Technology
                </div>
                <div className="text-gray-800 font-medium" data-id="element-96">
                  Solves the fundamental absorption problem. Reduces
                  inflammation. Restores cellular communication. Allows
                  ingredients to work where collagen is produced.
                </div>
              </div>
            </div>
          </div>

          {/* Product Intro */}
          <div className="bg-[#fffbeb] p-8 rounded-2xl border border-yellow-200 my-12 text-center" data-id="element-97">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4" data-id="element-98">
              Introducing Zenessa Firming Serum
            </h2>
            <p className="mb-6" data-id="element-99">
              When Dr. Smith's research became clear, she partnered with
              skincare scientists to create a formula combining grounding
              technology with proven anti-aging ingredients—gentle enough for
              sensitive, mature skin.
            </p>
            <img src="https://cdn.magicpatterns.com/uploads/6fHBuTdnMZ5EUKdwBMDCs8/Anti-aging_Serum_fur_empfindliche_Haut_(1).png" alt="Zenessa Serum Bottle" className="w-64 h-64 object-contain mx-auto rounded-xl mb-8 transform hover:scale-105 transition-transform" data-id="element-100" />
            <p className="font-bold text-lg mb-6" data-id="element-101">
              After three years of development and clinical trials on women aged
              45-68, the result is Zenessa Firming Serum.
            </p>
            <CtaButton fullWidth data-id="element-102">Check Availability & Pricing</CtaButton>
          </div>

          {/* How It Works */}
          <h2 className="font-serif text-3xl font-bold text-gray-900 mt-12 mb-8" data-id="element-103">
            How Zenessa Works
          </h2>

          <div className="space-y-8" data-id="element-104">
            <div className="flex gap-4" data-id="element-105">
              <div className="flex-shrink-0 w-12 h-12 bg-[#0d9488] text-white rounded-full flex items-center justify-center font-bold text-xl" data-id="element-106">
                1
              </div>
              <div data-id="element-107">
                <h4 className="font-bold text-lg text-gray-900" data-id="element-108">
                  Phase 1: Prepare (Weeks 1-2)
                </h4>
                <p className="text-gray-600" data-id="element-109">
                  Grounding technology reduces inflammation blocking absorption.
                  Your skin changes at cellular level. Most women report skin
                  feels more supple, hydrated, "alive."
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-110">
              <div className="flex-shrink-0 w-12 h-12 bg-[#0d9488] text-white rounded-full flex items-center justify-center font-bold text-xl" data-id="element-111">
                2
              </div>
              <div data-id="element-112">
                <h4 className="font-bold text-lg text-gray-900" data-id="element-113">
                  Phase 2: Penetrate (Weeks 3-6)
                </h4>
                <p className="text-gray-600" data-id="element-114">
                  With inflammation reduced, ingredients penetrate to where
                  collagen is produced. You see visible changes: subtle firming
                  along jawline, softening fine lines, improved texture.
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-115">
              <div className="flex-shrink-0 w-12 h-12 bg-[#0d9488] text-white rounded-full flex items-center justify-center font-bold text-xl" data-id="element-116">
                3
              </div>
              <div data-id="element-117">
                <h4 className="font-bold text-lg text-gray-900" data-id="element-118">
                  Phase 3: Produce (Weeks 7-12)
                </h4>
                <p className="text-gray-600" data-id="element-119">
                  Skin cells ramp up collagen production from within.
                  Transformation becomes undeniable. Firmer contours. Reduced
                  wrinkles. Smoother texture. Natural radiance.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-10" data-id="element-120">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-3" data-id="element-121">
              <Check className="text-green-500 w-6 h-6 flex-shrink-0" data-id="element-122" />
              <span className="font-medium" data-id="element-123">
                Visibly firmer jawline and cheeks
              </span>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-3" data-id="element-124">
              <Check className="text-green-500 w-6 h-6 flex-shrink-0" data-id="element-125" />
              <span className="font-medium" data-id="element-126">
                Reduced fine lines and crow's feet
              </span>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-3" data-id="element-127">
              <Check className="text-green-500 w-6 h-6 flex-shrink-0" data-id="element-128" />
              <span className="font-medium" data-id="element-129">Smoother, even texture</span>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-3" data-id="element-130">
              <Check className="text-green-500 w-6 h-6 flex-shrink-0" data-id="element-131" />
              <span className="font-medium" data-id="element-132">Natural radiance</span>
            </div>
          </div>

          {/* Clinical Results */}
          <Section className="bg-slate-50 p-8 rounded-2xl border border-slate-200" data-id="element-133">
            <h2 className="font-serif text-2xl font-bold text-center mb-8" data-id="element-134">
              Dr. Smith's Clinical Study
            </h2>
            <p className="text-center mb-8 text-gray-600" data-id="element-135">
              127 women aged 45-68 used Zenessa for 12 weeks. Results measured
              with dermatological equipment:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center" data-id="element-136">
              <div data-id="element-137">
                <div className="text-4xl font-bold text-[#0d9488] mb-2" data-id="element-138">
                  34%
                </div>
                <p className="text-sm font-bold text-gray-800" data-id="element-139">
                  Improvement in Firmness
                </p>
                <p className="text-xs text-gray-500" data-id="element-140">(cutometer readings)</p>
              </div>
              <div data-id="element-141">
                <div className="text-4xl font-bold text-[#0d9488] mb-2" data-id="element-142">
                  28%
                </div>
                <p className="text-sm font-bold text-gray-800" data-id="element-143">
                  Reduction in Fine Lines
                </p>
                <p className="text-xs text-gray-500" data-id="element-144">(PRIMOS imaging)</p>
              </div>
              <div data-id="element-145">
                <div className="text-4xl font-bold text-[#0d9488] mb-2" data-id="element-146">
                  89%
                </div>
                <p className="text-sm font-bold text-gray-800" data-id="element-147">
                  Would Continue Using
                </p>
                <p className="text-xs text-gray-500" data-id="element-148">(patient survey)</p>
              </div>
            </div>
            <p className="text-center text-sm text-gray-400 mt-6 italic" data-id="element-149">
              These aren't marketing claims—these are clinical measurements.
            </p>
          </Section>

          {/* Testimonials */}
          <h2 className="font-serif text-3xl font-bold text-gray-900 mt-16 mb-8 text-center" data-id="element-150">
            Real Stories from Real Women
          </h2>

          <Testimonial name="Linda" age="52" image="https://cdn.magicpatterns.com/uploads/njjH68ozgR8KaEsKvoQ1ix/media-eba85f07b392.jpg" quote="I still have wrinkles—I'm 52. But I look like a refreshed version of myself. And I got here without injections or thousands of dollars." result="Canceled Botox appointment at Week 12" data-id="element-151" />

          <BeforeAfter label="Linda's Results - Week 12" image1="https://cdn.magicpatterns.com/uploads/76QeLAnkhNDHST7oTaiMEd/Screenshot_2026-01-27_210500.png" image2="https://cdn.magicpatterns.com/uploads/ue2f7V43RGNZgVMmJLtiET/Screenshot_2026-01-27_210513.png" caption="Noticeable firming along the jawline and reduction in deep lines." data-id="element-152" />

          <Testimonial name="Patricia" age="58" image="https://images.unsplash.com/photo-1585076641399-5c06d1b3365f?auto=format&fit=crop&q=80&w=200&h=200" quote="No reaction. Not a single moment of redness or discomfort. And around week five, my crepey neck looked noticeably smoother. I actually cried." result="Sensitive skin approved" data-id="element-153" />

          <Testimonial name="Susan" age="61" image="https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&q=80&w=200&h=200" quote="I'm not saying it's identical to Botox. But it's good enough that I don't need it. And I'm saving hundreds of dollars every few months." result="Saved $800 on procedures" data-id="element-154" />

          <div className="my-12 text-center" data-id="element-155">
            <CtaButton className="mx-auto" data-id="element-156">
              Try Zenessa Risk-Free Today
            </CtaButton>
            <p className="text-sm text-gray-500 mt-3" data-id="element-157">
              60-Day Money-Back Guarantee • Free Shipping
            </p>
          </div>

          {/* FAQ Section */}
          <h2 className="font-serif text-3xl font-bold text-gray-900 mt-16 mb-8" data-id="element-158">
            Addressing Your Doubts
          </h2>

          <div className="space-y-6" data-id="element-159">
            <div className="border-l-4 border-gray-200 pl-6 py-2" data-id="element-160">
              <h4 className="font-bold text-lg text-gray-900 mb-2" data-id="element-161">
                "I've tried dozens of products—why should this work?"
              </h4>
              <p className="text-gray-600" data-id="element-162">
                You're right to ask. Those products failed because of the
                absorption problem. Zenessa solves this first using grounding
                technology, creating conditions where ingredients can actually
                penetrate.
              </p>
            </div>
            <div className="border-l-4 border-gray-200 pl-6 py-2" data-id="element-163">
              <h4 className="font-bold text-lg text-gray-900 mb-2" data-id="element-164">
                "How do I know this won't irritate my sensitive skin?"
              </h4>
              <p className="text-gray-600" data-id="element-165">
                Legitimate concern. Zenessa has no harsh retinoids or aggressive
                acids. In clinical trials, fewer than 2% reported any
                irritation, and it was mild and temporary.
              </p>
            </div>
            <div className="border-l-4 border-gray-200 pl-6 py-2" data-id="element-166">
              <h4 className="font-bold text-lg text-gray-900 mb-2" data-id="element-167">
                "This seems too good to be true—what's the catch?"
              </h4>
              <p className="text-gray-600" data-id="element-168">
                The "catch" is consistency. You must use it daily for 8-12 weeks
                for real results. It is not an overnight miracle or a surgical
                replacement, but a scientifically-backed solution for cellular
                absorption.
              </p>
            </div>
          </div>

          {/* Pricing Section */}
          <div id="offer" className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mt-16" data-id="element-169">
            <div className="bg-[#0d9488] text-white text-center py-4" data-id="element-170">
              <h3 className="text-xl font-bold uppercase tracking-wider" data-id="element-171">
                Special Reader Offer
              </h3>
              <p className="text-teal-100 text-sm" data-id="element-172">
                Available for a limited time
              </p>
            </div>

            <div className="p-8" data-id="element-173">
              <div className="flex flex-col md:flex-row gap-8 items-center justify-center mb-8" data-id="element-174">
                <img src="https://cdn.magicpatterns.com/uploads/6fHBuTdnMZ5EUKdwBMDCs8/Anti-aging_Serum_fur_empfindliche_Haut_(1).png" alt="Zenessa Bundle" className="w-48 h-48 object-contain" data-id="element-175" />
                <div className="text-center md:text-left" data-id="element-176">
                  <h4 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-177">
                    3-Month Supply (Recommended)
                  </h4>
                  <p className="text-gray-600 mb-4" data-id="element-178">
                    Best value for the full 12-week transformation.
                  </p>
                  <div className="flex items-center justify-center md:justify-start gap-3 mb-4" data-id="element-179">
                    <span className="text-gray-400 line-through text-lg" data-id="element-180">
                      $100.00
                    </span>
                    <span className="text-3xl font-bold text-[#0d9488]" data-id="element-181">
                      $49.95
                    </span>
                    <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full" data-id="element-182">
                      SAVE 50%
                    </span>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600 mb-6 text-left" data-id="element-183">
                    <li className="flex items-center gap-2" data-id="element-184">
                      <Check className="w-4 h-4 text-green-500" data-id="element-185" /> 3 Bottles of
                      Zenessa Serum
                    </li>
                    <li className="flex items-center gap-2" data-id="element-186">
                      <Check className="w-4 h-4 text-green-500" data-id="element-187" /> Free
                      Anti-Aging Guide ($29 value)
                    </li>
                    <li className="flex items-center gap-2" data-id="element-188">
                      <Check className="w-4 h-4 text-green-500" data-id="element-189" /> Free Priority
                      Shipping
                    </li>
                  </ul>
                </div>
              </div>

              <CtaButton fullWidth className="text-xl py-5" data-id="element-190">
                Get Zenessa 30% OFF Today
              </CtaButton>

              <div className="flex flex-wrap justify-center gap-4 mt-6 text-xs text-gray-500" data-id="element-191">
                <div className="flex items-center gap-1" data-id="element-192">
                  <ShieldCheck className="w-4 h-4" data-id="element-193" /> 60-Day Money-Back
                  Guarantee
                </div>
                <div className="flex items-center gap-1" data-id="element-194">
                  <Clock className="w-4 h-4" data-id="element-195" /> Secure Checkout
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 text-center text-sm text-gray-500 border-t border-gray-100" data-id="element-196">
              Also available: 1-Month Supply ($67) and 6-Month Supply ($300)
            </div>
          </div>

          {/* Guarantee */}
          <div className="flex flex-col md:flex-row gap-6 items-center bg-yellow-50 p-6 rounded-xl border border-yellow-200 mt-12" data-id="element-197">
            <div className="flex-shrink-0" data-id="element-198">
              <div className="w-24 h-24 bg-yellow-400 rounded-full flex items-center justify-center text-yellow-900 font-bold text-center text-xs border-4 border-yellow-200 shadow-sm" data-id="element-199">
                100%
                <br data-id="element-200" />
                MONEY
                <br data-id="element-201" />
                BACK
              </div>
            </div>
            <div data-id="element-202">
              <h4 className="font-bold text-lg text-gray-900 mb-2" data-id="element-203">
                Try Zenessa Risk-Free for 60 Days
              </h4>
              <p className="text-gray-700 text-sm" data-id="element-204">
                Use the entire bottle. If you're not satisfied for any reason,
                contact us for a full refund. No questions asked. We offer this
                because we've seen the clinical results.
              </p>
            </div>
          </div>

          {/* Final Thoughts */}
          <div className="mt-16" data-id="element-205">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6" data-id="element-206">
              You Deserve to Feel Confident Again
            </h2>
            <p data-id="element-207">
              This isn't really about wrinkles, is it? It's about looking in the
              mirror and recognizing yourself. Feeling confident in meetings,
              gatherings, photos. Refusing to become invisible just because
              you're over 50.
            </p>
            <p className="mt-4" data-id="element-208">
              You've earned the right to age gracefully. But you've also earned
              the right to age vibrantly, radiantly, confidently.
            </p>

            <div className="mt-8 p-6 bg-gray-100 rounded-lg italic text-gray-700" data-id="element-209">
              <p className="font-bold mb-2" data-id="element-210">P.S.</p>
              <p data-id="element-211">
                This isn't another retinol cream. This is grounding technology
                solving the absorption problem. Dr. Smith's clinical results:
                34% firmness improvement, 28% line reduction—measured, not
                claimed.
              </p>
            </div>
          </div>
        </article>

        {/* Footer */}
        <footer className="mt-20 pt-10 border-t border-gray-200 text-center text-xs text-gray-400" data-id="element-212">
          <p className="mb-4" data-id="element-213">
            These statements have not been evaluated by the FDA. This product is
            not intended to diagnose, treat, cure, or prevent any disease.
            Results may vary. Individual results depend on consistent use over
            8-12 weeks.
          </p>
          <div className="flex justify-center gap-4 mb-4" data-id="element-214">
            <a href="#" className="hover:underline" data-id="element-215">
              Privacy Policy
            </a>
            <a href="#" className="hover:underline" data-id="element-216">
              Terms of Service
            </a>
            <a href="#" className="hover:underline" data-id="element-217">
              Contact Us
            </a>
            <a href="#" className="hover:underline" data-id="element-218">
              Scientific References
            </a>
          </div>
          <p data-id="element-219">&copy; 2026 Zenessa Skincare. All rights reserved.</p>
        </footer>
      </main>
    </div>;
}